/** New consumer plan codes */
export type PlanCode =
  | "BASIC"
  | "PLUS"
  | "PRO"
  | "ABSOLUTE"
  | "WONDA"
  | "WONDA_PREMIUM";

/** Legacy plan codes (for existing subscribers) */
export type LegacyPlanCode = "FREE" | "CREATOR" | "PREMIUM";

/**
 * Grandfathered catalog codes that exist in the billing_plan table but aren't
 * surfaced as selectable new/legacy options. Kept in PLAN_FEATURES so existing
 * subscribers never see a raw code as their plan label.
 */
export type GrandfatheredPlanCode = "SKILL" | "500";

/** All valid plan codes (new + legacy) */
export type AnyPlanCode = PlanCode | LegacyPlanCode;

type PlanFeatures = {
  label: string;
  isPaid: boolean;
  allowsTeamInvites: boolean;
  description?: string;
};

export const PLAN_FEATURES: Record<
  AnyPlanCode | GrandfatheredPlanCode,
  PlanFeatures
> = {
  // New consumer plans
  BASIC: {
    label: "Basic",
    isPaid: false,
    allowsTeamInvites: false,
    description: "Images + editing with basic credits",
  },
  PLUS: {
    label: "Plus",
    isPaid: true,
    allowsTeamInvites: false,
    description: "Images + videos + templates",
  },
  PRO: {
    label: "Pro",
    isPaid: true,
    allowsTeamInvites: true,
    description: "Full access with priority features",
  },
  ABSOLUTE: {
    label: "Absolute",
    isPaid: true,
    allowsTeamInvites: true,
    description: "Maximum credits and all features",
  },
  // CLI plans
  WONDA: {
    label: "Pro",
    isPaid: true,
    allowsTeamInvites: false,
    description: "Skills, priority runs, and publishing",
  },
  WONDA_PREMIUM: {
    label: "Premium",
    isPaid: true,
    allowsTeamInvites: false,
    description: "Everything in Pro plus antidetect and shadow ban protection",
  },
  // Legacy plans
  FREE: {
    label: "Free",
    isPaid: false,
    allowsTeamInvites: false,
    description: "Starter access with limited credits",
  },
  CREATOR: {
    label: "Creator",
    isPaid: true,
    allowsTeamInvites: true,
    description: "Creator subscription tier",
  },
  PREMIUM: {
    label: "Premium",
    isPaid: true,
    allowsTeamInvites: true,
    description: "Premium subscription tier",
  },
  // Grandfathered catalog plans
  SKILL: {
    label: "Skill",
    isPaid: true,
    allowsTeamInvites: false,
  },
  "500": {
    label: "Degaus 500",
    isPaid: true,
    allowsTeamInvites: false,
  },
};

/** New paid plan codes */
export const PAID_PLAN_CODES: AnyPlanCode[] = [
  "PLUS",
  "PRO",
  "ABSOLUTE",
  "WONDA",
  "WONDA_PREMIUM",
  "CREATOR",
  "PREMIUM",
];

export function checkIsPaidPlan(planCode: string | null): boolean {
  return planCode !== null && PAID_PLAN_CODES.includes(planCode as AnyPlanCode);
}

/**
 * Plan codes an admin may comp to a user for free. Matches the current public
 * pricing page, which sells one plan (Pro). Premium (cloud twins) is parked and
 * legacy codes are intentionally not grantable; existing holders keep them.
 */
export const COMP_GRANTABLE_PLAN_CODES = ["WONDA"] as const;

export type CompGrantablePlanCode = (typeof COMP_GRANTABLE_PLAN_CODES)[number];

/** Human label for a plan code, falling back to the raw code if unknown. */
export function getPlanLabel(planCode: string): string {
  return (
    PLAN_FEATURES[planCode as keyof typeof PLAN_FEATURES]?.label ?? planCode
  );
}

/**
 * 403 message for a caller without cloud-twin entitlement. Cloud twins are
 * parked: no plan sells them anymore, so the gate points at the local WAB
 * rather than an upgrade. Existing Premium holders still pass the gate.
 */
export const CLOUD_TWIN_UNAVAILABLE_MESSAGE =
  "Cloud twins are not available. Run automations on your own machine with the local WAB instead.";
